import { useState, useEffect, useCallback } from 'react';
import { Platform } from '../../interfaces/db';
import { EXTENSION_ID, MESSAGE_TYPES } from '../../cfg/endpoints';

export interface IntegrationStatus {
  isExtensionInstalled: boolean;
  isExtensionActivated: boolean;
  hasPagePermissions: boolean;
  hasSecondarySetup: boolean;
  isFullyActive: boolean;
  statusMessage: string;
  actionRequired: IntegrationAction;
}

export enum IntegrationAction {
  INSTALL_EXTENSION = 'INSTALL_EXTENSION',
  ACTIVATE_EXTENSION = 'ACTIVATE_EXTENSION',
  GRANT_PERMISSIONS = 'GRANT_PERMISSIONS',
  COMPLETE_SECONDARY_SETUP = 'COMPLETE_SECONDARY_SETUP',
  UPGRADE_TO_PARALLEL = 'UPGRADE_TO_PARALLEL',
  NONE = 'NONE'
}

interface ExtensionInfo {
  version?: number[];
  isActivated?: boolean;
  activatedPages?: string[];
}

// Platform-specific configuration
const PLATFORM_CONFIG: Partial<Record<Platform, { domains: string[]; requiresSecondarySetup: boolean; }>> = {
  [Platform.HUBSPOT]: {
    domains: ['hubspot.com', 'app.hubspot.com'],
    requiresSecondarySetup: false
  },
  [Platform.SALESLOFT]: {
    domains: ['salesloft.com', 'app.salesloft.com'],
    requiresSecondarySetup: false
  },
  [Platform.OUTREACH]: {
    domains: ['outreach.io', 'web.outreach.io'],
    requiresSecondarySetup: false
  },
  [Platform.APOLLO]: {
    domains: ['apollo.io', 'app.apollo.io'],
    requiresSecondarySetup: false
  },
  [Platform.SALESFORCE]: {
    domains: ['salesforce.com', 'lightning.force.com'],
    requiresSecondarySetup: false
  },
  [Platform.CLAY]: {
    domains: ['clay.com', 'app.clay.com'],
    requiresSecondarySetup: false
  },
  [Platform.GOOGLE_SHEETS]: {
    domains: ['docs.google.com'],
    requiresSecondarySetup: false
  },
  [Platform.GOHIGHLEVEL]: {
    domains: ['gohighlevel.com', 'app.gohighlevel.com'],
    requiresSecondarySetup: true
  },
  [Platform.AMPLEMARKET]: {
    domains: ['amplemarket.com', 'app.amplemarket.com'],
    requiresSecondarySetup: false
  },
  [Platform.ZOHO]: {
    domains: ['zoho.com', 'crm.zoho.com'],
    requiresSecondarySetup: false
  },
  // Additional dialer platforms
  [Platform.AIRCALL]: {
    domains: ['aircall.io', 'dashboard.aircall.io'],
    requiresSecondarySetup: false
  },
  [Platform.ALOWARE]: {
    domains: ['aloware.com', 'app.aloware.com'],
    requiresSecondarySetup: false
  },
  [Platform.BITRIX]: {
    domains: ['bitrix24.com', 'bitrix24.net'],
    requiresSecondarySetup: false
  },
  [Platform.BLOOBIRDS]: {
    domains: ['bloobirds.com', 'app.bloobirds.com'],
    requiresSecondarySetup: false
  },
  [Platform.CALLTOOLS]: {
    domains: ['calltools.com', 'app.calltools.com'],
    requiresSecondarySetup: false
  },
  [Platform.CONNECT_AND_SELL]: {
    domains: ['connectandsell.com', 'app.connectandsell.com'],
    requiresSecondarySetup: false
  },
  [Platform.DIALPAD]: {
    domains: ['dialpad.com', 'app.dialpad.com'],
    requiresSecondarySetup: false
  },
  [Platform.FIVENINE]: {
    domains: ['five9.com', 'app.five9.com'],
    requiresSecondarySetup: false
  },
  [Platform.KOMMO]: {
    domains: ['kommo.com', 'app.kommo.com'],
    requiresSecondarySetup: false
  },
  [Platform.NOOKS]: {
    domains: ['nooks.in', 'app.nooks.in'],
    requiresSecondarySetup: false
  },
  [Platform.ORUM]: {
    domains: ['orum.com', 'app.orum.com'],
    requiresSecondarySetup: false
  },
  [Platform.NEXTIVA]: {
    domains: ['nextiva.com', 'app.nextiva.com'],
    requiresSecondarySetup: false
  },
  [Platform.RINGCENTRAL]: {
    domains: ['ringcentral.com', 'app.ringcentral.com'],
    requiresSecondarySetup: false
  },
  [Platform.OPENPHONE]: {
    domains: ['openphone.com', 'app.openphone.com'],
    requiresSecondarySetup: false
  },
  [Platform.VANILLASOFT]: {
    domains: ['vanillasoft.com', 'app.vanillasoft.com'],
    requiresSecondarySetup: false
  }
};

export function useIntegrationStatus(platform: Platform): IntegrationStatus {
  const [status, setStatus] = useState<IntegrationStatus>({
    isExtensionInstalled: false,
    isExtensionActivated: false,
    hasPagePermissions: false,
    hasSecondarySetup: false,
    isFullyActive: false,
    statusMessage: 'Checking...',
    actionRequired: IntegrationAction.INSTALL_EXTENSION
  });

  const [extensionInfo, setExtensionInfo] = useState<ExtensionInfo | null>(null);

  // Check if extension is installed
  const checkExtensionInstalled = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!window.chrome || !window.chrome.runtime) {
        resolve(false);
        return;
      }

      try {
        window.chrome.runtime.sendMessage(
          EXTENSION_ID,
          { type: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_EXTENSION_API_KEY_AND_EXTENSION_INFO },
          (response) => {
            if (window.chrome.runtime.lastError) {
              resolve(false);
            } else {
              resolve(!!response);
            }
          }
        );
      } catch (error) {
        resolve(false);
      }
    });
  }, []);

  // Request extension info via message passing
  const requestExtensionInfo = useCallback((): Promise<ExtensionInfo | null> => {
    return new Promise((resolve) => {
      if (!window.chrome || !window.chrome.runtime) {
        resolve(null);
        return;
      }

      try {
        window.chrome.runtime.sendMessage(
          EXTENSION_ID,
          { 
            type: MESSAGE_TYPES.EXTERNAL_TO_BACKGROUND_REQUEST_EXTENSION_API_KEY_AND_EXTENSION_INFO,
            requestInfo: true
          },
          (response) => {
            if (window.chrome.runtime.lastError || !response) {
              resolve(null);
            } else {
              resolve({
                version: response.version,
                isActivated: response.isActivated,
                activatedPages: response.activatedPages || []
              });
            }
          }
        );
      } catch (error) {
        resolve(null);
      }
    });
  }, []);

  // Check if platform domains have permissions
  const checkPagePermissions = useCallback((activatedPages: string[], platform: Platform): boolean => {
    const config = PLATFORM_CONFIG[platform];
    if (!config || !activatedPages) return false;

    // Check if any of the platform's domains are in the activated pages
    return config.domains.some((domain: string) => 
      activatedPages.some((page: string) => 
        page.includes(domain) || page === '*'
      )
    );
  }, []);

  // Check secondary setup requirements
  const checkSecondarySetup = useCallback((platform: Platform): boolean => {
    // Only GoHighLevel requires additional API key setup
    if (platform === Platform.GOHIGHLEVEL) {
      // TODO: Check if GoHighLevel API key is configured
      return false; // Assume not configured for now
    }
    
    // All other platforms only need extension installed and activated
    return true;
  }, []);

  // Generate status message based on current state
  const generateStatusMessage = useCallback((
    installed: boolean,
    activated: boolean,
    hasPermissions: boolean,
    hasSecondary: boolean,
    platform: Platform
  ): { message: string; action: IntegrationAction } => {
    if (!installed) {
      return {
        message: 'Extension required - Install Trellus extension to enable this integration',
        action: IntegrationAction.INSTALL_EXTENSION
      };
    }

    if (!activated) {
      return {
        message: 'Extension inactive - Activate the Trellus extension',
        action: IntegrationAction.ACTIVATE_EXTENSION
      };
    }

    if (!hasPermissions) {
      const config = PLATFORM_CONFIG[platform];
      const domains = config?.domains.join(', ') || 'platform';
      return {
        message: `Permissions required - Grant extension access to ${domains}`,
        action: IntegrationAction.GRANT_PERMISSIONS
      };
    }

    if (!hasSecondary) {
      return {
        message: 'Setup required - Complete additional configuration',
        action: IntegrationAction.COMPLETE_SECONDARY_SETUP
      };
    }

    return {
      message: 'Active - Integration is fully configured',
      action: IntegrationAction.NONE
    };
  }, []);

  // Main effect to check integration status
  useEffect(() => {
    let isMounted = true;

    const checkStatus = async () => {
      try {
        // Step 1: Check if extension is installed
        const installed = await checkExtensionInstalled();
        
        if (!isMounted) return;

        if (!installed) {
          setStatus({
            isExtensionInstalled: false,
            isExtensionActivated: false,
            hasPagePermissions: false,
            hasSecondarySetup: false,
            isFullyActive: false,
            statusMessage: 'Extension required - Install Trellus extension to enable this integration',
            actionRequired: IntegrationAction.INSTALL_EXTENSION
          });
          return;
        }

        // Step 2: Get extension info
        const info = await requestExtensionInfo();
        
        if (!isMounted) return;

        setExtensionInfo(info);

        const activated = info?.isActivated ?? false;
        const hasPagePermissions = info ? checkPagePermissions(info.activatedPages || [], platform) : false;
        const hasSecondary = checkSecondarySetup(platform);
        const isFullyActive = installed && activated && hasPagePermissions && hasSecondary;

        const { message, action } = generateStatusMessage(
          installed,
          activated,
          hasPagePermissions,
          hasSecondary,
          platform
        );

        setStatus({
          isExtensionInstalled: installed,
          isExtensionActivated: activated,
          hasPagePermissions,
          hasSecondarySetup: hasSecondary,
          isFullyActive,
          statusMessage: message,
          actionRequired: action
        });

      } catch (error) {
        if (!isMounted) return;
        
        setStatus({
          isExtensionInstalled: false,
          isExtensionActivated: false,
          hasPagePermissions: false,
          hasSecondarySetup: false,
          isFullyActive: false,
          statusMessage: 'Unable to check integration status',
          actionRequired: IntegrationAction.INSTALL_EXTENSION
        });
      }
    };

    checkStatus();

    return () => {
      isMounted = false;
    };
  }, [platform, checkExtensionInstalled, requestExtensionInfo, checkPagePermissions, checkSecondarySetup, generateStatusMessage]);

  return status;
}

// Hook for checking multiple integrations at once
// Global extension status interface
export interface GlobalExtensionStatus {
  installed: boolean;
  activated: boolean;
  activatedEverywhere: boolean;
  checking: boolean;
}

// Hook for global extension status (used by the UI banners)
export function useGlobalExtensionStatus(): GlobalExtensionStatus {
  const [status, setStatus] = useState<GlobalExtensionStatus>({
    installed: false,
    activated: false,
    activatedEverywhere: false,
    checking: true
  });

  useEffect(() => {
    let isMounted = true;
    
    const checkExtensionStatus = async () => {
      if (!isMounted) return;
      
      setStatus(prev => ({ ...prev, checking: true }));
      
      try {
        // Check if Chrome API exists first
        if (!window.chrome || !window.chrome.runtime || !window.chrome.runtime.sendMessage) {
          if (isMounted) {
            setStatus({ installed: false, activated: false, activatedEverywhere: false, checking: false });
          }
          return;
        }
        
        
        // Try to send message to extension with timeout - this will tell us if it's installed AND activated
        const activationResponse = await Promise.race([
          new Promise<any>((resolve, reject) => {
            window.chrome.runtime.sendMessage(
              EXTENSION_ID,
              { type: 'EXTERNAL_TO_BACKGROUND_IS_ACTIVATED' },
              (response) => {
                if (window.chrome.runtime.lastError) {
                  reject(new Error('Extension not installed'));
                } else {
                  resolve(response);
                }
              }
            );
          }),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 5000)
          )
        ]);

        if (!isMounted) return;
        
        const activated = !!activationResponse;
        
        let activatedEverywhere = false;

        // If activated, check if it's activated everywhere (needed for GoHighLevel)
        if (activated) {
          try {
            const everywhereResponse = await Promise.race([
              new Promise<any>((resolve, reject) => {
                window.chrome.runtime.sendMessage(
                  EXTENSION_ID,
                  { type: 'EXTERNAL_TO_BACKGROUND_IS_ACTIVATED_EVERYWHERE' },
                  (response) => {
                    if (window.chrome.runtime.lastError) {
                      reject(new Error('Everywhere check failed'));
                    } else {
                      resolve(response);
                    }
                  }
                );
              }),
              new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), 3000)
              )
            ]);
            activatedEverywhere = !!everywhereResponse;
          } catch (error) {
            activatedEverywhere = false;
          }
        }

        if (!isMounted) return;

        // If we get here, extension is installed
        const finalStatus = {
          installed: true,
          activated,
          activatedEverywhere,
          checking: false
        };
        
        setStatus(finalStatus);

      } catch (error) {
        if (isMounted) {
          setStatus({ installed: false, activated: false, activatedEverywhere: false, checking: false });
        }
      }
    };

    checkExtensionStatus();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return status;
}

